// #402 — Resolves the "billed party" shown in the confirmation summary: the
// entity a course is actually invoiced to. Always defined — unlike a
// BillingAddress, which is optional. See CONTEXT.md ("Adresse de facturation").
//
// This MIRRORS the API's resolution cascade; the API is the source of truth
// (BillingCalculator::getClientDetails() + both PDF generators). Reordering the
// cascade there silently makes this summary lie — no test spans both repos.
// See docs/adr/0002-recap-miroir-cascade-facturation.md.
//
// Returns i18n keys, not text, so it stays pure and framework-free.

// Mirrors PHP `!empty($client->invoices_company)`, which is how the API picks
// this branch. Over the string domain, empty() is true only for '' and '0' — a
// whitespace-only string is NOT empty, and still sends the invoice down the
// company branch. Handles the nullable field itself rather than relying on the
// caller to normalize.
const hasCompanyBilling = (invoicesCompany) =>
  !!invoicesCompany && invoicesCompany !== '0';

const splitCompanyLines = (invoicesCompany) =>
  invoicesCompany
    .split(',')
    .map((line) => line.trim())
    .filter(Boolean);

const formatClientName = (client) =>
  [client && client.prenom, client && client.nom]
    .filter(Boolean)
    .join(' ')
    .trim();

export const resolveBilledParty = ({ billingAddress, client, position }) => {
  if (billingAddress && billingAddress.id) {
    return {
      source: 'billing_address',
      name: billingAddress.billing_name,
      lines: [
        billingAddress.address_line1,
        [billingAddress.postal_code, billingAddress.city]
          .filter(Boolean)
          .join(' '),
        // The invoice prints the country only when it isn't FR
        // (course-invoice.blade.php). BillingAddressCard applies the same rule
        // but inlines it with the city; here it is its own line, mirroring the
        // invoice's own <div>.
        billingAddress.country !== 'FR' ? billingAddress.country : null,
      ].filter(Boolean),
      noteKey: null,
    };
  }

  const name = formatClientName(client);
  const invoicesCompany = (client && client.invoices_company) || '';

  if (hasCompanyBilling(invoicesCompany)) {
    return {
      source: 'invoices_company',
      name,
      lines: splitCompanyLines(invoicesCompany),
      noteKey: 'repairconfirmation.billing.company',
    };
  }

  return {
    source: 'intervention',
    name,
    lines: [position].filter(Boolean),
    noteKey: 'repairconfirmation.billing.same_as_intervention',
  };
};

export default resolveBilledParty;
